/**
 * AdminCreditsGuideEditor.jsx — Premium Institutional CMS (Library + Assignments)
 * V2 — Bibliothèque multi-guides + assignations aux espaces.
 * Design System O'SCOP Premium — Archetype Jewel & Luxury (Institutional Variant)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Plus, Loader2, Settings, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import CreditsPricingGuide from './CreditsPricingGuide';
import ExperimentsPanel from './ExperimentsPanel';
import { FONTS, emptyConfig, apiUrl, authHeaders, Card } from './creditsGuideShared';
import LibraryView from './CreditsGuideLibraryView';
import EditorView from './CreditsGuideEditorView';

const AdminCreditsGuideEditor = () => {
  const [library, setLibrary] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [meta, setMeta] = useState({ icons: [], colors: [] });
  const [editingGuide, setEditingGuide] = useState(null); // null = library view, object = editor view
  const [experimentsOpen, setExperimentsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newBase, setNewBase] = useState('default');
  const [creating, setCreating] = useState(false);

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/admin/credits-guides'), { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLibrary(data.library || []);
      setAssignments(data.assignments || {});
    } catch (e) {
      toast.error(`Échec chargement bibliothèque : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLibrary();
    fetch(apiUrl('/api/admin/credits-guides/_meta/defaults'), { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setMeta)
      .catch(() => {});
  }, [fetchLibrary]);

  const openEditor = async (guideId) => {
    try {
      const res = await fetch(apiUrl(`/api/admin/credits-guides/${guideId}`), { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEditingGuide({
        ...data,
        header: data.header || emptyConfig().header,
        free_actions: data.free_actions || [],
        categories: data.categories || [],
        how_it_works: data.how_it_works || [],
        credit_types: data.credit_types || [],
      });
      setDirty(false);
    } catch (e) {
      toast.error(`Échec chargement guide : ${e.message}`);
    }
  };

  const handleSave = async () => {
    if (!editingGuide) return;
    setSaving(true);
    try {
      const payload = {
        name: editingGuide.name,
        description: editingGuide.description || '',
        header: editingGuide.header,
        free_actions: editingGuide.free_actions,
        categories: editingGuide.categories,
        how_it_works: editingGuide.how_it_works,
        credit_types: editingGuide.credit_types,
      };
      const res = await fetch(apiUrl(`/api/admin/credits-guides/${editingGuide.id}`), {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setDirty(false);
      toast.success(`Guide « ${editingGuide.name} » enregistré`);
      fetchLibrary();
    } catch (e) {
      toast.error(`Échec sauvegarde : ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(apiUrl('/api/admin/credits-guides'), {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ name: newName, description: newDescription, base_factory: newBase }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(`Guide « ${data.guide.name} » créé`);
      setCreateOpen(false);
      setNewName(''); setNewDescription(''); setNewBase('default');
      await fetchLibrary();
      openEditor(data.guide.id);
    } catch (e) {
      toast.error(`Échec création : ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (guideId) => {
    try {
      const res = await fetch(apiUrl(`/api/admin/credits-guides/${guideId}/duplicate`), { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(`Copie créée : ${data.guide.name}`);
      fetchLibrary();
    } catch (e) {
      toast.error(`Échec duplication : ${e.message}`);
    }
  };

  const handleDelete = async (guide) => {
    if (guide.is_factory) { toast.error('Guide factory protégé'); return; }
    if ((guide.assigned_spaces || []).length > 0) {
      toast.error('Ce guide est assigné. Réassignez les espaces d\'abord.');
      return;
    }
    if (!window.confirm(`Supprimer définitivement le guide « ${guide.name} » ?`)) return;
    try {
      const res = await fetch(apiUrl(`/api/admin/credits-guides/${guide.id}`), { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Guide supprimé');
      fetchLibrary();
    } catch (e) {
      toast.error(`Échec suppression : ${e.message}`);
    }
  };

  const handleAssign = async (space, guideId) => {
    try {
      const res = await fetch(apiUrl(`/api/admin/credits-guides/assignments/${space}`), {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ guide_id: guideId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success(`Espace ${space} → ${library.find((g) => g.id === guideId)?.name}`);
      fetchLibrary();
    } catch (e) {
      toast.error(`Échec assignation : ${e.message}`);
    }
  };

  const totalAssigned = useMemo(() => Object.keys(assignments).length, [assignments]);
  const totalActions = useMemo(() => library.reduce((s, g) => s + (g.categories || []).reduce((ss, c) => ss + (c.actions?.length || 0), 0), 0), [library]);

  return (
    <div className="space-y-5" data-testid="admin-credits-guide-editor" style={FONTS.body}>
      {/* ===== HERO ===== */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1e3f] via-[#0B3C5D] to-[#0a1e3f] text-white shadow-lg ring-1 ring-slate-200">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24]/60 to-transparent" />
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#fbbf24]/10 blur-3xl pointer-events-none" />

        <div className="relative p-5 md:p-6 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0">
            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-lg">
              <Settings className="h-7 w-7 text-[#fbbf24]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#fbbf24]/90 font-medium mb-1" style={FONTS.outfit}>O'SCOP · CMS Officiel</div>
              <h1 className="text-2xl md:text-3xl font-bold leading-tight tracking-tight" style={FONTS.playfair}>Guide des Crédits ESS</h1>
              <p className="text-sm text-white/75 mt-1">Bibliothèque de guides personnalisables, assignables à chaque espace utilisateur.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => setExperimentsOpen(true)}
              size="sm"
              className="bg-[#fbbf24]/15 hover:bg-[#fbbf24]/25 text-[#fbbf24] border border-[#fbbf24]/40"
              data-testid="open-experiments-btn"
            >
              <FlaskConical className="h-4 w-4 mr-1.5" />Expériences A/B
            </Button>
            <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Guides</div>
              <div className="text-xl font-bold tabular-nums" style={FONTS.outfit}>{library.length}</div>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Assignations</div>
              <div className="text-xl font-bold tabular-nums" style={FONTS.outfit}>{totalAssigned}/4</div>
            </div>
            <div className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-white/60" style={FONTS.outfit}>Actions</div>
              <div className="text-xl font-bold text-[#fbbf24] tabular-nums" style={FONTS.outfit}>{totalActions}</div>
            </div>
          </div>
        </div>
      </div>

      {loading && !editingGuide && (
        <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" />Chargement…</div>
      )}

      {/* ===== VIEW ROUTER ===== */}
      {experimentsOpen && (
        <Card className="p-5 md:p-6">
          <ExperimentsPanel library={library} onClose={() => setExperimentsOpen(false)} />
        </Card>
      )}

      {!experimentsOpen && !editingGuide && !loading && (
        <LibraryView
          library={library}
          assignments={assignments}
          totalAssigned={totalAssigned}
          onEdit={openEditor}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
          onAssign={handleAssign}
          onCreate={() => setCreateOpen(true)}
        />
      )}

      {!experimentsOpen && editingGuide && (
        <EditorView
          guide={editingGuide}
          meta={meta}
          dirty={dirty}
          saving={saving}
          onBack={() => {
            if (dirty && !window.confirm('Modifications non sauvegardées. Revenir à la bibliothèque ?')) return;
            setEditingGuide(null);
            setDirty(false);
          }}
          onSave={handleSave}
          onPreview={() => setPreviewOpen(true)}
          setGuide={setEditingGuide}
          setDirty={setDirty}
        />
      )}

      {/* ===== CREATE DIALOG ===== */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-guide-dialog">
          <DialogHeader>
            <DialogTitle style={FONTS.outfit}>Nouveau guide</DialogTitle>
            <DialogDescription>Créez un guide vierge basé sur un modèle factory, puis personnalisez-le.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Nom *</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Guide Cooper Premium" className="mt-1" data-testid="new-guide-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Description (interne)</Label>
              <Textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} placeholder="À quoi sert ce guide ?" className="mt-1" rows={2} data-testid="new-guide-description" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500" style={FONTS.outfit}>Modèle de base</Label>
              <Select value={newBase} onValueChange={setNewBase}>
                <SelectTrigger className="mt-1" data-testid="new-guide-base"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard (sans actions Expert)</SelectItem>
                  <SelectItem value="expert">Expert (inclut Production Expert)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Annuler</Button>
            <Button onClick={handleCreate} disabled={creating || !newName.trim()} className="bg-[#0a1e3f] hover:bg-[#0B3C5D] text-white" data-testid="confirm-create-guide">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== PREVIEW DIALOG ===== */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>Aperçu du guide</DialogTitle>
            <DialogDescription>Visualisation en temps réel du guide « {editingGuide?.name} ».</DialogDescription>
          </DialogHeader>
          {editingGuide && (
            <CreditsPricingGuide
              userRole="expert"
              previewConfig={editingGuide}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCreditsGuideEditor;
